import { useState } from 'react'
import { Head, Link, usePage } from '@inertiajs/react'
import { usePublicPage } from '@/hooks/usePublicPage'
import { LandingNavbar } from '@/components/landing/LandingNavbar'
import { LandingFooter } from '@/components/landing/LandingFooter'
import { Clock, User, Tag, ChevronRight, ArrowLeft, ArrowRight, Share2, Copy, Check } from 'lucide-react'
import type { PageProps } from '@/types'

interface BlogPost {
  id: number
  title: string
  slug: string
  excerpt: string
  content: string
  cover_image_url?: string
  author_name: string
  category: string
  exam_tags: string[]
  published_at: string
  read_minutes: number
  seo_title: string
  seo_description: string
  seo_keywords?: string
}

interface BlogCard {
  id: number
  title: string
  slug: string
  excerpt: string
  cover_image_url?: string
  author_name: string
  category: string
  published_at: string
  read_minutes: number
}

interface BlogShowProps {
  post: BlogPost
  related_posts: BlogCard[]
}

const CATEGORY_LABELS: Record<string, string> = {
  announcement:     'Announcements',
  exam_guide:       'Exam Guide',
  preparation_tips: 'Prep Tips',
  general:          'General',
}

const CATEGORY_COLORS: Record<string, string> = {
  announcement:     'bg-[#fef3c7] text-[#92400e] border-[#fde68a]',
  exam_guide:       'bg-[#dbeafe] text-[#1e40af] border-[#bfdbfe]',
  preparation_tips: 'bg-[#d1fae5] text-[#065f46] border-[#a7f3d0]',
  general:          'bg-[#f1f5f9] text-[#475569] border-[#e2e8f0]',
}

export default function BlogShow({ post, related_posts }: BlogShowProps) {
  const { auth } = usePage<PageProps>().props
  usePublicPage()

  const [copied, setCopied] = useState(false)

  const postUrl = typeof window !== 'undefined'
    ? window.location.href
    : `https://prepkiya.com/blog/${post.slug}`

  const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(`${post.title}\n${postUrl}`)}`
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(postUrl)}`
  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(postUrl)}`

  const handleCopyLink = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(postUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    }
  }

  const handleNativeShare = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: postUrl,
        })
      } catch (_) {}
    } else {
      handleCopyLink()
    }
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.seo_description,
    url: `https://prepkiya.com/blog/${post.slug}`,
    datePublished: post.published_at,
    author: {
      '@type': 'Organization',
      name: post.author_name
    },
    publisher: {
      '@type': 'EducationalOrganization',
      name: 'PrepKiya',
      url: 'https://prepkiya.com',
      logo: 'https://prepkiya.com/landing-logo.png'
    },
    ...(post.cover_image_url ? { image: post.cover_image_url } : {})
  }

  return (
    <>
      <Head title={`${post.seo_title} | PrepKiya Blog`}>
        <meta name="description" content={post.seo_description} />
        {post.seo_keywords && <meta name="keywords" content={post.seo_keywords} />}
        <link rel="canonical" href={`https://prepkiya.com/blog/${post.slug}`} />
        <meta property="og:title" content={post.seo_title} />
        <meta property="og:description" content={post.seo_description} />
        <meta property="og:url" content={`https://prepkiya.com/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        {post.cover_image_url && <meta property="og:image" content={post.cover_image_url} />}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      <div className="min-h-screen w-full flex flex-col bg-white text-slate-900 antialiased selection:bg-[#c8933b]/20 selection:text-slate-900">
        <LandingNavbar user={auth?.user} />

        <main className="w-full flex-1 flex flex-col">

          {/* Breadcrumb */}
          <div className="w-full bg-white border-b border-slate-100">
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
              <nav className="!font-sans flex items-center gap-1.5 text-xs text-slate-500">
                <Link href="/" className="hover:text-[#131b2e] transition-colors">Home</Link>
                <ChevronRight className="w-3 h-3" />
                <Link href="/blog" className="hover:text-[#131b2e] transition-colors">Blog</Link>
                <ChevronRight className="w-3 h-3" />
                <span className="text-[#131b2e] font-medium line-clamp-1 max-w-xs">{post.title}</span>
              </nav>
            </div>
          </div>

          {/* Cover Image */}
          {post.cover_image_url && (
            <div className="w-full max-w-4xl mx-auto px-3.5 sm:px-6 mt-4 sm:mt-8">
              <div className="w-full overflow-hidden rounded-2xl shadow-sm border border-slate-100 bg-[#0f172a] aspect-[16/9] max-h-[480px]">
                <img
                  src={post.cover_image_url}
                  alt={post.title}
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          )}

          {/* Article */}
          <article className="w-full py-6 sm:py-12">
            <div className="w-full max-w-3xl mx-auto px-3.5 sm:px-6">

              {/* Category + Tags */}
              <div className="flex flex-wrap items-center gap-2 mb-5">
                <span className={`!font-sans inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${CATEGORY_COLORS[post.category] || CATEGORY_COLORS.general}`}>
                  {CATEGORY_LABELS[post.category] || post.category}
                </span>
                {post.exam_tags.map((tag) => (
                  <span key={tag} className="!font-sans inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#f4f7fb] border border-slate-200 text-slate-600">
                    <Tag className="w-2.5 h-2.5" /> {tag}
                  </span>
                ))}
              </div>

              {/* Title */}
              <h1 className="!font-sans text-2xl sm:text-3xl lg:text-4xl font-black text-[#131b2e] leading-tight tracking-tight mb-5">
                {post.title}
              </h1>

              {/* Meta bar with Quick Share */}
              <div className="!font-sans flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-sm text-slate-500 pb-6 border-b border-slate-100 mb-8">
                <div className="flex flex-wrap items-center gap-4 text-xs sm:text-sm">
                  <span className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" />
                    {post.author_name}
                  </span>
                  <span>{post.published_at}</span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    {post.read_minutes} min read
                  </span>
                </div>

                {/* Quick Share Icons */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-xs font-semibold text-slate-400 mr-1 hidden sm:inline">Share:</span>
                  
                  {/* WhatsApp */}
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Share on WhatsApp"
                    className="w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 text-slate-600 flex items-center justify-center transition-all shadow-3xs cursor-pointer"
                  >
                    <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                      <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.711 2.598 2.664-.699c.969.53 1.777.79 2.796.79h.005c3.18 0 5.767-2.587 5.767-5.766.001-3.181-2.586-5.766-5.772-5.766zm3.374 8.24c-.147.417-.852.793-1.196.84-.339.046-.78.074-1.258-.081-.309-.1-.703-.239-1.218-.465-2.193-.96-3.626-3.188-3.736-3.336-.109-.148-.891-1.183-.891-2.257 0-1.074.561-1.603.761-1.824.2-.22.436-.276.582-.276.146 0 .292.002.418.008.134.006.313-.051.49.373.183.439.623 1.516.678 1.626.055.11.092.239.019.385-.074.147-.11.239-.22.368-.11.129-.232.288-.331.386-.11.109-.225.228-.097.448.128.22.569.939 1.22 1.519.839.747 1.547.98 1.767 1.089.22.11.349.092.477-.055.129-.147.55-.642.697-.862.147-.22.293-.184.495-.11.202.073 1.284.606 1.504.716.22.11.367.165.422.257.055.092.055.534-.092.951z"/>
                    </svg>
                  </a>

                  {/* LinkedIn */}
                  <a
                    href={linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Share on LinkedIn"
                    className="w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-sky-50 hover:text-[#0A66C2] hover:border-sky-200 text-slate-600 flex items-center justify-center transition-all shadow-3xs cursor-pointer"
                  >
                    <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                      <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v7.6h2.8v-7.6h-2.8M7.86 6.5a1.63 1.63 0 0 0-1.64 1.63c0 .9.73 1.64 1.64 1.64s1.63-.74 1.63-1.64c0-.9-.73-1.63-1.63-1.63Z"/>
                    </svg>
                  </a>

                  {/* X */}
                  <a
                    href={xUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Share on X"
                    className="w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 hover:text-slate-900 hover:border-slate-300 text-slate-600 flex items-center justify-center transition-all shadow-3xs cursor-pointer"
                  >
                    <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>

                  {/* Copy Link */}
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    title={copied ? "Link copied to clipboard!" : "Copy link"}
                    className="h-8 px-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 flex items-center gap-1.5 text-xs font-medium transition-all shadow-3xs cursor-pointer"
                  >
                    {copied ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                        <span className="text-emerald-600 font-semibold text-2xs">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5 text-slate-500" />
                        <span className="hidden sm:inline text-2xs">Copy</span>
                      </>
                    )}
                  </button>

                  {/* Native Share (Mobile) */}
                  <button
                    type="button"
                    onClick={handleNativeShare}
                    title="Share"
                    className="sm:hidden w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 flex items-center justify-center transition-all shadow-3xs cursor-pointer"
                  >
                    <Share2 className="w-3.5 h-3.5 text-slate-600" />
                  </button>
                </div>
              </div>

              {/* Content body — rendered as HTML with external link protection */}
              <div
                className="!font-sans prose-blog text-slate-700 leading-relaxed space-y-4 sm:space-y-5 break-words overflow-x-hidden"
                style={{
                  fontSize: '1.0625rem',
                  lineHeight: '1.8',
                }}
                onClick={(e) => {
                  const anchor = (e.target as HTMLElement).closest('a');
                  if (!anchor) return;
                  const href = anchor.getAttribute('href');
                  if (!href) return;
                  try {
                    const url = new URL(href, window.location.origin);
                    if (url.origin !== window.location.origin) {
                      e.preventDefault();
                      window.open(href, '_blank', 'noopener,noreferrer');
                    }
                  } catch (_) {}
                }}
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Share Article Section */}
              <div className="mt-12 p-6 sm:p-7 rounded-2xl bg-[#f8fafc] border border-slate-200 shadow-2xs space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#b5802a]">
                      <Share2 className="w-3.5 h-3.5" />
                      <span>Share & Help Aspirants</span>
                    </div>
                    <h3 className="!font-sans text-base sm:text-lg font-black text-[#131b2e] leading-snug">
                      Found this article helpful? Spread the word!
                    </h3>
                    <p className="!font-sans text-xs sm:text-sm text-slate-600">
                      Share this guide with friends or in your exam preparation study groups.
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2.5">
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold bg-[#25D366] hover:bg-[#20bd5a] text-white transition-all shadow-xs"
                    >
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                        <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.711 2.598 2.664-.699c.969.53 1.777.79 2.796.79h.005c3.18 0 5.767-2.587 5.767-5.766.001-3.181-2.586-5.766-5.772-5.766zm3.374 8.24c-.147.417-.852.793-1.196.84-.339.046-.78.074-1.258-.081-.309-.1-.703-.239-1.218-.465-2.193-.96-3.626-3.188-3.736-3.336-.109-.148-.891-1.183-.891-2.257 0-1.074.561-1.603.761-1.824.2-.22.436-.276.582-.276.146 0 .292.002.418.008.134.006.313-.051.49.373.183.439.623 1.516.678 1.626.055.11.092.239.019.385-.074.147-.11.239-.22.368-.11.129-.232.288-.331.386-.11.109-.225.228-.097.448.128.22.569.939 1.22 1.519.839.747 1.547.98 1.767 1.089.22.11.349.092.477-.055.129-.147.55-.642.697-.862.147-.22.293-.184.495-.11.202.073 1.284.606 1.504.716.22.11.367.165.422.257.055.092.055.534-.092.951z"/>
                      </svg>
                      <span>WhatsApp</span>
                    </a>

                    <a
                      href={linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold bg-[#0A66C2] hover:bg-[#084e96] text-white transition-all shadow-xs"
                    >
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                        <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v7.6h2.8v-7.6h-2.8M7.86 6.5a1.63 1.63 0 0 0-1.64 1.63c0 .9.73 1.64 1.64 1.64s1.63-.74 1.63-1.64c0-.9-.73-1.63-1.63-1.63Z"/>
                      </svg>
                      <span>LinkedIn</span>
                    </a>

                    <a
                      href={xUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold bg-[#0f172a] hover:bg-black text-white transition-all shadow-xs"
                    >
                      <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      <span>Post on X</span>
                    </a>

                    <button
                      type="button"
                      onClick={handleCopyLink}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-all shadow-xs cursor-pointer"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 text-emerald-600" />
                          <span className="text-emerald-700">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 text-slate-500" />
                          <span>Copy Link</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Back link */}
              <div className="mt-8 pt-8 border-t border-slate-100">
                <Link
                  href="/blog"
                  className="!font-sans inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#131b2e] transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Blog
                </Link>
              </div>
            </div>
          </article>

          {/* Related Posts */}
          {related_posts.length > 0 && (
            <section className="w-full py-12 bg-[#f8fafc] border-t border-slate-100">
              <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="!font-sans text-xl font-black text-[#131b2e] mb-6">Related Articles</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {related_posts.map((rp) => (
                    <Link
                      key={rp.id}
                      href={`/blog/${rp.slug}`}
                      className="group block bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-xs hover:shadow-md transition-shadow"
                    >
                      {rp.cover_image_url ? (
                        <div className="w-full h-36 overflow-hidden">
                          <img src={rp.cover_image_url} alt={rp.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        </div>
                      ) : (
                        <div className="w-full h-16 bg-[#131b2e] px-4 flex items-center justify-between border-b border-slate-800">
                          <span className="text-[11px] font-bold text-slate-200 uppercase tracking-wider">{CATEGORY_LABELS[rp.category] || rp.category}</span>
                          <span className="text-[10px] font-medium text-slate-400">PrepKiya</span>
                        </div>
                      )}
                      <div className="p-4 space-y-2">
                        <h3 className="!font-sans text-sm font-bold text-[#131b2e] group-hover:text-[#c8933b] transition-colors line-clamp-2 leading-snug">{rp.title}</h3>
                        <div className="!font-sans flex items-center justify-between text-xs text-slate-400">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {rp.read_minutes} min</span>
                          <span className="flex items-center gap-1 font-semibold text-[#c8933b]">Read <ArrowRight className="w-3 h-3" /></span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <LandingFooter />
      </div>
    </>
  )
}
